'use client';

import { miniCartNavigation } from '@components/MiniCart/miniCartClient';
import { CommerceQueryProvider } from '@contexts/CommerceQueryProvider';
import { ExposureEvent } from '@lib/flags/amplitude/ExposureEvent';
import { Flags } from '@lib/flags/flagConstants';
import { followRenderedActiveOrg } from '@lib/util/activeOrgCookie';
import { useEffect } from 'react';
import { SnackBar, UpmProductDetailTemplate } from 'ui-components';
import { CommerceCartProvider } from './CommerceCartProvider';
import { UpmProductDetailPageProps } from './types';
import { useUpmProductDetailPage } from './useUpmProductDetailPage';

const UpmProductDetailPageInner = ({
  microcopy,
  ...initialData
}: UpmProductDetailPageProps) => {
  const { data, actions, cartToast, onDismissCartToast } =
    useUpmProductDetailPage(initialData);

  return (
    <>
      {data.productInfo.entitlement === 'not-entitled' &&
        Boolean(data.productInfo.price.originalPrice) &&
        !data.previewMode && <ExposureEvent flagName={Flags.MULTI_CART} />}
      <UpmProductDetailTemplate
        {...data}
        microcopy={microcopy}
        {...actions}
        className="container pt-4 lg:pt-12"
      />
      {cartToast?.kind === 'already-in-cart' && (
        <SnackBar
          key={cartToast.key}
          icon="ShoppingCart"
          closeIcon="X"
          onClose={onDismissCartToast}
          microcopy={{
            title: microcopy.cartToast.alreadyInCart,
            closeAriaLabel: microcopy.cartToast.closeAriaLabel,
          }}
        />
      )}
      {cartToast?.kind === 'cart-conflict' && (
        <SnackBar
          key={cartToast.key}
          icon="WarningCircle"
          closeIcon="X"
          onClose={onDismissCartToast}
          microcopy={{
            title: microcopy.cartToast.cartConflict,
            closeAriaLabel: microcopy.cartToast.closeAriaLabel,
          }}
        />
      )}
      {cartToast?.kind === 'organization-changed' && (
        <SnackBar
          key={cartToast.key}
          icon="WarningCircle"
          closeIcon="X"
          duration={Infinity}
          onActionClick={miniCartNavigation.reload}
          onClose={onDismissCartToast}
          microcopy={{
            title: microcopy.cartToast.organizationChanged,
            actionLabel: microcopy.cartToast.reload,
            closeAriaLabel: microcopy.cartToast.closeAriaLabel,
          }}
        />
      )}
    </>
  );
};

export const UpmProductDetailPage = (props: UpmProductDetailPageProps) => {
  const { activeOrganizationId } = props;
  // The header count and the mini-cart work in the org this page was rendered
  // for, as its entitlement CTAs and its cart do (CPS-7696).
  useEffect(() => {
    if (activeOrganizationId) followRenderedActiveOrg(activeOrganizationId);
  }, [activeOrganizationId]);

  if (!props.productSku) {
    return (
      <CommerceQueryProvider>
        <UpmProductDetailPageInner {...props} />
      </CommerceQueryProvider>
    );
  }

  return (
    <CommerceCartProvider activeOrganizationId={activeOrganizationId}>
      <UpmProductDetailPageInner {...props} />
    </CommerceCartProvider>
  );
};
